import { UiEventType } from '@enums/game-events.enum';
import { ActionType } from '@enums/node-types.enum';
import { CollectibleInfo, Collectible } from '@models/collectibles';
import { InventoryItem, EconomyResponse } from '@models/economy';
import {
  StageInfo,
  GameStartPosition,
  ActionInfo,
  Goal,
  GoalState,
  GoalWithState,
} from '@models/game-config.interface';
import { HistoryRecord } from '@models/user-history';
import { TranslationService } from '@services/core/TranslationService';
import { Singleton } from '@utils/singleton';
import Phaser from 'phaser';

@Singleton
export default class GameState {
  private events = new Phaser.Events.EventEmitter();

  // --- User location ---
  private position = 0;

  // --- Roll settings. Get after each roll ---
  private nextRollTime: number = 0;
  private isAbleToRollNow = false;
  private trs = new TranslationService();

  // --- User progression ---
  collectibles: Collectible[] = [];
  xp = 0;
  coins = 0;
  goals: GoalWithState[] = [];
  claimedCollectiblesPrizes: HistoryRecord[] = [];
  claimedInstantPrizes: HistoryRecord[] = [];

  // --- Status ---
  stageCompleted: boolean = false;

  visitedNodes = new Set<number>();
  completedEvents = new Set<string>();
  rewardNodes = new Set<number>(); // fill this from the board model
  checkpointNodes = new Set<number>(); // fill this from the board model
  lostRewardNodes = new Set<number>(); // should render grey
  claimedRewardNodes = new Set<number>(); // "claimed" state

  resetStage(
    game: StageInfo,
    startPosition: GameStartPosition,
    goalStates: GoalState[],
  ) {
    this.position = startPosition.tile;
    this.nextRollTime = startPosition.nextRoll;
    this.isAbleToRollNow = startPosition.rollsAvailable > 0;
    this.goals = this.getGoalsWithState(game.boardGoals, goalStates);
    this.stageCompleted = false;
    this.visitedNodes.clear();
    this.completedEvents.clear();
    this.rewardNodes.clear();
    this.checkpointNodes.clear();
    this.lostRewardNodes.clear();
    this.claimedRewardNodes.clear();

    const action = game.actionsConfig?.actions ?? [];
    const actionMap = new Map(action.map((a) => [a.uuid, a.actionType]));

    if (game.pathConfig) {
      Object.keys(game.pathConfig.nodes).forEach((nodeIndex) => {
        const actionType = actionMap.get(game.pathConfig!.nodes[nodeIndex]);
        if (
          actionType &&
          [ActionType.Prize, ActionType.Collectible].includes(actionType)
        ) {
          this.rewardNodes.add(Number(nodeIndex));
        }
        if (actionType === ActionType.Checkpoint) {
          this.checkpointNodes.add(Number(nodeIndex));
        }
      });
    }
  }

  initCollectibles(
    collectibles: CollectibleInfo[],
    claimedCollectibles: InventoryItem[],
  ) {
    this.collectibles = collectibles.map((c) => {
      const data = claimedCollectibles.find((cc) => cc.uuid === c.uuid);
      if (data) {
        return {
          ...c,
          value: data.quantity,
        };
      }
      return {
        ...c,
        value: 0,
      };
    });
  }

  syncEconomy(actions: ActionInfo[], economy: EconomyResponse): void {
    this.claimedCollectiblesPrizes = [];
    this.claimedInstantPrizes = [];
    economy.prizes.forEach((p) => {
      const selectedAction = actions.find((a) => a.uuid === p.actionUuid);
      if (selectedAction) {
        if (
          selectedAction.actionType === ActionType.Collectible &&
          selectedAction.collectible?.prize?.uuid === p.prizeUuid
        ) {
          const max = selectedAction.collectible.targetCount;
          this.claimedCollectiblesPrizes.push({
            name: `${max}/${max} ${selectedAction.collectible.name}`,
            prize: selectedAction.collectible.prize,
            quantity: p.quantity,
            assetUrl: selectedAction.collectible.iconUrl,
          });
        }
        if (
          selectedAction.prize &&
          selectedAction.prize!.uuid === p.prizeUuid
        ) {
          // prize im mandatory here
          this.claimedInstantPrizes.push({
            name: selectedAction.gameRulesLabel,
            assetUrl: selectedAction.symbolUrl,
            prize: selectedAction.prize,
            quantity: p.quantity,
          });
        }
      }
    });
    this.coins = economy.coins;
    this.xp = economy.xp;
  }

  getPosition(): number {
    return this.position;
  }

  isAbleToRoll(): boolean {
    return this.isAbleToRollNow;
  }

  getNextRollTime(): number {
    return this.nextRollTime;
  }

  isRewardLost(node: number): boolean {
    return this.lostRewardNodes.has(node);
  }

  isCheckpointReached(node: number): boolean {
    return this.checkpointNodes.has(node);
  }

  private visit(node: number) {
    if (node > 0) this.visitedNodes.add(node);
  }

  // Called when player steps on a node during animation BUT doesn't finish there. */
  passThrough(node: number) {
    this.visit(node);

    // Passing a reward node makes it lost (grey), unless already claimed
    if (this.rewardNodes.has(node) && !this.claimedRewardNodes.has(node)) {
      this.lostRewardNodes.add(node);
    }
  }

  // Called only for the final landing node (including backwards via setPosition). */
  moveTo(node: number) {
    this.position = node;
    this.visit(node);

    // Landing can claim a reward if it wasn't lost
    if (this.rewardNodes.has(node) && !this.lostRewardNodes.has(node)) {
      this.claimedRewardNodes.add(node);
    }
  }

  setRollInfo(nextRoll: number, rollsAvailable: number): void {
    this.nextRollTime = nextRoll;
    this.isAbleToRollNow = rollsAvailable > 0;
    this.events.emit(UiEventType.ROLLS_CHANGED, this.isAbleToRollNow);
  }

  addPoints(points: number, action: ActionInfo): void {
    this.xp += points;
    this.events.emit(UiEventType.XP_GAINED, {
      uuid: action.uuid,
      message: `+ ${points} ${this.trs.translate('points.pts').toUpperCase()}`,
    });
  }

  claimCollectible(value: number, action: ActionInfo): void {
    const collectible = action.collectible!;
    this.collectibles = this.collectibles.map((c) =>
      c.uuid === collectible.uuid
        ? { ...c, value: Math.min((c.value ?? 0) + value, c.targetCount) }
        : c,
    );
    const updated = this.collectibles.find((c) => c.uuid === collectible.uuid);
    if (updated && updated.value >= updated.targetCount) {
      this.events.emit(UiEventType.COLLECTIBLE_GAINED_FULL, {
        collectibles: this.collectibles,
        collectible: updated,
        message: `+ ${collectible.name}`,
      });
    } else {
      this.events.emit(UiEventType.COLLECTIBLE_GAINED, {
        collectibles: this.collectibles,
        uuid: collectible.uuid,
        message: `+ ${collectible.name}`,
      });
    }
  }

  requestPopup(text: string, imagePath?: string): void {
    this.events.emit(UiEventType.POPUP_REQUESTED, { text, imagePath });
  }

  claimInstantPrize(action: ActionInfo): void {
    this.events.emit(UiEventType.CLAIM_INSTANT_PRIZE, { action });
  }

  completeMilestone(action: ActionInfo): void {
    this.events.emit(UiEventType.COMPLETE_MILESTONE, { action });
  }

  completeChapter() {
    this.stageCompleted = true;
  }

  // Event System
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, fn: (...args: any[]) => void, context?: any) {
    this.events.on(event, fn, context);
  }

  // Unsubscribe from game state events
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(event: string, fn: (...args: any[]) => void, context?: any) {
    this.events.off(event, fn, context);
  }

  private getGoalsWithState(
    goals: Goal[],
    goalStates: GoalState[],
  ): GoalWithState[] {
    return (goals || []).map((goal) => {
      const currentGoalState = (goalStates || []).find(
        (g) => g.uuid === goal.uuid,
      );
      return {
        ...goal,
        completed: currentGoalState?.completed ?? false,
      };
    });
  }
}
